import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'config/redux/store';
import { getUserRole } from 'utils/auth/auth';

/**
 * Routes a user is allowed to visit even without an active subscription.
 * Anything else redirects to /subscriptions so they can pick a plan.
 */
const ALLOWED_PATHS_WHEN_EXPIRED = [
  '/subscriptions',
  '/billing',
  '/help',
  '/support',
  '/payment/success',
  '/payment/cancel',
  '/logout',
  '/settings',
];

const isPathAllowed = (pathname: string): boolean => {
  return ALLOWED_PATHS_WHEN_EXPIRED.some(allowed => pathname === allowed || pathname.startsWith(`${allowed}/`));
};

const isAdminOrSuperAdmin = (): boolean => {
  const role = getUserRole();
  if (!role) return false;
  const normalized = role.toLowerCase().replace(/\s+/g, '');
  return normalized === 'admin' || normalized === 'superadmin';
};

/**
 * Wraps page content. When the user has no active subscription (expired) and is not
 * an Admin/SuperAdmin, redirects to /subscriptions unless they're already on a whitelisted page.
 */
const SubscriptionGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isExpired = useSelector((state: RootState) => state.user.isExpired);

  if (!isExpired) return <>{children}</>;
  if (isAdminOrSuperAdmin()) return <>{children}</>;
  if (isPathAllowed(location.pathname)) return <>{children}</>;

  return <Navigate to="/subscriptions" replace />;
};

export default SubscriptionGuard;
